import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser, userDisplayNameAsString } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { learningDifficultyMetricsKeys } from '../../config/configListing';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ReviewRating,
  IconPerson,
  IconBook,
  IconNoteBook,
  IconCalendar,
  IconMessage,
  IconCheckCircle,
  IconCheck,
} from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', description = '', price, publicData } = currentListing.attributes;
  const {
    teachingMetrics,
    subjects,
    ADHDExperience,
    dyslexiaExperience,
    motivationExperience,
    ratings,
    hasFreeLesson = false,
  } = publicData || {};
  const hasTeachingMetrics = !!teachingMetrics && Object.keys(teachingMetrics).length > 0;
  const hasSubjects = !!subjects && subjects.length > 0;
  const learningDifficultyMetrics = {
    ADHS: ADHDExperience,
    Dyslexia: dyslexiaExperience,
    Motivation: motivationExperience,
  };
  const hasLearningDifficultyMetrics =
    (ADHDExperience && ADHDExperience !== 'noExperience') ||
    (dyslexiaExperience && dyslexiaExperience !== 'noExperience') ||
    (motivationExperience && motivationExperience !== 'noExperience');
  const hasRatings = ratings && Object.keys(ratings).length > 0;
  const rating = ratings?.rating.toFixed(1) || 0;
  const reviewsCount = ratings?.count || 0;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = userDisplayNameAsString(author, '');
  const authorImage = author?.profileImage;
  const firstImage = authorImage;
  const {
    aspectWidth = 3,
    aspectHeight = 2,
    variantPrefix = 'scaled-large',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  let showLearningDifficultyIcon = false;
  const learningDifficultyTotalScore = (
    <span className={css.learningDifficultyTotalScore}> / 10</span>
  );
  return (
    <div className={classes}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <NamedLink name="ListingPage" params={{ id, slug }}>
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        </NamedLink>
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.mainInfo}>
          {!!hasFreeLesson && (
            <div className={css.freeLesson}>
              <IconCheck />
              <FormattedMessage id="ListingCard.freeLesson" />
            </div>
          )}
          {showAuthorInfo ? (
            <NamedLink name="ListingPage" params={{ id, slug }}>
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            </NamedLink>
          ) : null}
          <div className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>
          {/* {hasRatings ? (
            <div className={css.rating}>
              <span>
                <ReviewRating rating={rating} />
              </span>
              <span className={css.ratingValue}>
                <FormattedMessage id="ListingCard.rating" values={{ rating: rating }} />
              </span>
              {!!reviewsCount || reviewsCount === 0 ? (
                <span className={css.reviewsCount}>
                  <FormattedMessage
                    id="ListingCard.reviews"
                    values={{ count: <span className={css.bold}>{reviewsCount}</span> }}
                  />
                </span>
              ) : null}
            </div>
          ) : null} */}

          {hasTeachingMetrics ? (
            <div className={css.teachingMetrics}>
              <IconPerson className={css.teachingMetricsIcon} />
              <span>
                <FormattedMessage
                  id="ListingCard.teachingMetrics"
                  values={{
                    activeStudents: teachingMetrics?.activeStudents,
                    totalLessons: teachingMetrics?.totalLessons,
                  }}
                />
              </span>
            </div>
          ) : null}

          {hasSubjects ? (
            <div className={css.subjects}>
              <IconBook className={css.subjectsIcon} />
              <span>{subjects.join(', ')}</span>
            </div>
          ) : null}

          {hasLearningDifficultyMetrics ? (
            <div className={css.learningDifficultyMetrics}>
              {Object.keys(learningDifficultyMetricsKeys).map((key, index) => {
                const learningDifficulty = learningDifficultyMetrics?.[key];
                const canHelpWithLearningDifficulty =
                  key === 'ADHS' && learningDifficulty && learningDifficulty !== 'noExperience';
                const showIcon = !showLearningDifficultyIcon;
                if (showIcon) showLearningDifficultyIcon = true;
                return (
                  canHelpWithLearningDifficulty && (
                    <div className={css.learningDifficulty}>
                      <span className={css.learningDifficultyTitle}>
                        {showIcon ? (
                          <IconNoteBook className={css.learningDifficultyMetricsIcon} />
                        ) : (
                          <span className={css.spacer} />
                        )}
                        {key}
                      </span>
                      <span className={css.iconCheck}>
                        <IconCheckCircle />
                      </span>
                    </div>
                  )
                );
              })}
            </div>
          ) : null}
          <div className={css.description}>
            {richText(description, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </div>

          <div className={css.ctas}>
            {/* <NamedLink
              className={css.schedule}
              name="ListingPage"
              params={{ id, slug }}
              to={{ hash: '#schedule' }}
            >
              <IconCalendar className={css.iconCalendar} />
            </NamedLink> */}
            <NamedLink
              className={css.message}
              name="ListingPage"
              params={{ id, slug }}
              to={{ hash: '#message' }}
            >
              <IconMessage className={css.iconMessage} />
            </NamedLink>
            <NamedLink className={css.seeProfile} name="ListingPage" params={{ id, slug }}>
              <FormattedMessage id="ListingCard.seeProfile" />
            </NamedLink>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
